import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError, rupees } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Save, History, Info } from "lucide-react";

export default function Rates() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [rates, setRates] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/rates").then((r) => setRates(r.data));
    api.get("/rates/history").then((r) => setHistory(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!rates) {
    return <div className="space-y-4"><div className="skeleton h-12 w-64" /><div className="skeleton h-96" /></div>;
  }

  const setMetal = (k, v) => setRates({ ...rates, metal: { ...rates.metal, [k]: parseFloat(v) || 0 } });  const setPurity = (k, v) => setRates({ ...rates, purity_multipliers: { ...rates.purity_multipliers, [k]: parseFloat(v) || 0 } });
  const setLabour = (k, v) => setRates({ ...rates, labour: { ...rates.labour, [k]: parseFloat(v) || 0 } });
  const setT1 = (i, field, v) => {
    const t1 = [...rates.t1];
    t1[i] = { ...t1[i], [field]: parseFloat(v) || 0 };
    setRates({ ...rates, t1 });
  };
  const setSimpleTable = (key, i, field, v) => {
    const arr = [...rates[key]];
    arr[i] = { ...arr[i], [field]: parseFloat(v) || 0 };
    setRates({ ...rates, [key]: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...rates };
      delete payload._id;
      delete payload.version_number;
      const { data } = await api.put("/rates", payload);
      setRates(data);
      toast.success(`Saved as version ${data.version_number}`);
      api.get("/rates/history").then((r) => setHistory(r.data));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const ro = !canEdit;
  const F = (props) => <Field {...props} readOnly={ro} />;
  const ST = (props) => <SimpleTable {...props} readOnly={ro} />;

  return (
    <div className="space-y-10" data-testid="rates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Master Settings</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Rate Settings</h1>
          <p className="text-slate-500 mt-2">Update metals, labour, GST and diamond brackets. Changes reprice every product instantly.</p>
        </div>
        {canEdit && (
          <Button onClick={save} disabled={saving} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white h-11 px-6" data-testid="save-rates-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300 flex items-start gap-3" data-testid="rates-readonly-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">Viewing rate settings (read-only). Contact a Master Admin to modify rates.</p>
        </div>
      )}

      {/* Metal / Labour / Other */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card title="Metal Rates" testid="metal-card">
          <F label="Gold 24KT (₹/g)" value={rates.metal.gold_24kt} onChange={(v) => setMetal("gold_24kt", v)} testid="rate-gold" />
          <F label="Platinum (₹/g)" value={rates.metal.platinum} onChange={(v) => setMetal("platinum", v)} testid="rate-platinum" />
          <F label="Silver 925 (₹/g)" value={rates.metal.silver_925} onChange={(v) => setMetal("silver_925", v)} testid="rate-silver" />
        </Card>
        <Card title="Purity Multipliers">
          <F label="18KT" value={rates.purity_multipliers["18KT"]} onChange={(v) => setPurity("18KT", v)} step="0.001" testid="rate-18kt" />
          <F label="14KT" value={rates.purity_multipliers["14KT"]} onChange={(v) => setPurity("14KT", v)} step="0.001" testid="rate-14kt" />
          <F label="9KT" value={rates.purity_multipliers["9KT"]} onChange={(v) => setPurity("9KT", v)} step="0.001" testid="rate-9kt" />
        </Card>
        <Card title="Labour & Charges">
          <div className="mb-3 p-2 rounded-md border border-[#C9A84C]/20 bg-[#FAF8F3]" data-testid="labour-threshold-group">
            <F label="Minimum-labour weight threshold (grams)"
               value={rates.labour.min_labour_threshold_g ?? 1.25}
               onChange={(v) => setLabour("min_labour_threshold_g", v)}
               step="0.01"
               testid="rate-min-labour-threshold" />
            <p className="text-[10px] text-slate-500 mt-1">
              Pieces below this net weight are charged the tier&apos;s flat &ldquo;minimum labour&rdquo;; at or above, per-gram × weight. Boundary (exactly this value) uses per-gram.
            </p>
          </div>
          {(() => {
            const thr = Number(rates.labour.min_labour_threshold_g ?? 1.25);
            const thrLabel = Number.isFinite(thr) ? thr.toFixed(2).replace(/\.?0+$/, "") : "1.25";
            return (
          <>
          <div className="space-y-1" data-testid="labour-group-company">
            <F label={`Company labour (₹/g, ≥ ${thrLabel}g)`} value={rates.labour.company} onChange={(v) => setLabour("company", v)} testid="rate-co-labour" />
            <div className="pl-4 border-l-2 border-[#C9A84C]/30">
              <F label={`Company minimum labour (< ${thrLabel}g)`} value={rates.labour.company_min ?? rates.labour.company} onChange={(v) => setLabour("company_min", v)} testid="rate-co-labour-min" />
              <p className="text-[10px] text-slate-400 mt-1 -mt-2 mb-3">Flat charge applied when net weight is below {thrLabel} grams</p>
            </div>
          </div>
          <div className="space-y-1" data-testid="labour-group-franchise">
            <F label={`Franchise labour (₹/g, ≥ ${thrLabel}g)`} value={rates.labour.franchise} onChange={(v) => setLabour("franchise", v)} testid="rate-fr-labour" />
            <div className="pl-4 border-l-2 border-[#C9A84C]/30">
              <F label={`Franchise minimum labour (< ${thrLabel}g)`} value={rates.labour.franchise_min ?? rates.labour.franchise} onChange={(v) => setLabour("franchise_min", v)} testid="rate-fr-labour-min" />
              <p className="text-[10px] text-slate-400 mt-1 -mt-2 mb-3">Flat charge applied when net weight is below {thrLabel} grams</p>
            </div>
          </div>
          <div className="space-y-1" data-testid="labour-group-retailer">
            <F label={`Retailer labour (₹/g, ≥ ${thrLabel}g)`} value={rates.labour.retailer ?? 1350} onChange={(v) => setLabour("retailer", v)} testid="rate-rt-labour" />
            <div className="pl-4 border-l-2 border-[#C9A84C]/30">
              <F label={`Retailer minimum labour (< ${thrLabel}g)`} value={rates.labour.retailer_min ?? rates.labour.retailer ?? 1350} onChange={(v) => setLabour("retailer_min", v)} testid="rate-rt-labour-min" />
              <p className="text-[10px] text-slate-400 mt-1 -mt-2 mb-3">Flat charge applied when net weight is below {thrLabel} grams (distributor channel)</p>
            </div>
          </div>
          <div className="space-y-1" data-testid="labour-group-customer">
            <F label={`Customer labour (₹/g, ≥ ${thrLabel}g)`} value={rates.labour.customer} onChange={(v) => setLabour("customer", v)} testid="rate-cu-labour" />
            <div className="pl-4 border-l-2 border-[#C9A84C]/30">
              <F label={`Customer minimum labour (< ${thrLabel}g)`} value={rates.labour.customer_min ?? rates.labour.customer} onChange={(v) => setLabour("customer_min", v)} testid="rate-cu-labour-min" />
              <p className="text-[10px] text-slate-400 mt-1 -mt-2 mb-3">Flat charge applied when net weight is below {thrLabel} grams</p>
            </div>
          </div>
          </>
            );
          })()}
          <F label="GST rate (e.g. 0.03)" value={rates.gst_rate} onChange={(v) => setRates({ ...rates, gst_rate: parseFloat(v) || 0 })} step="0.001" testid="rate-gst" />
          <F label="Customer diamond multiplier" value={rates.customer_diamond_multiplier} onChange={(v) => setRates({ ...rates, customer_diamond_multiplier: parseFloat(v) || 0 })} step="0.01" testid="rate-cu-dia-mult" />
          <F label="Retailer diamond multiplier" value={rates.retailer_diamond_multiplier ?? 1.5} onChange={(v) => setRates({ ...rates, retailer_diamond_multiplier: parseFloat(v) || 0 })} step="0.01" testid="rate-rt-dia-mult" />
          <p className="text-[10px] text-slate-400 -mt-2 mb-1">Retailer diamond rate = Distributor sell rate × this multiplier</p>
        </Card>
      </div>

      {/* T1 Standard Shapes */}
      <div className="luxe-card p-6 overflow-x-auto">
        <h2 className="font-serif text-2xl text-[#0D1B2A] mb-4">T1 · Standard Shapes (per carat)</h2>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-2">Bracket</th>
              <th className="py-2 px-1 text-right">RD Sell</th><th className="py-2 px-1 text-right">RD Cost</th>
              <th className="py-2 px-1 text-right">Ashth Sell</th><th className="py-2 px-1 text-right">Ashth Cost</th>
              <th className="py-2 px-1 text-right">Oval Sell</th><th className="py-2 px-1 text-right">Oval Cost</th>
              <th className="py-2 px-1 text-right">EM Sell</th><th className="py-2 px-1 text-right">EM Cost</th>
            </tr>
          </thead>
          <tbody>
            {rates.t1.map((b, i) => (
              <tr key={`t1-${b.min}-${b.max}`} className="border-t border-[#0D1B2A]/5">
                <td className="py-2 pr-2 text-slate-600 whitespace-nowrap">{b.min.toFixed(3)}–{b.max.toFixed(3)}</td>
                {["rd_sell","rd_cost","ashth_sell","ashth_cost","oval_sell","oval_cost","em_sell","em_cost"].map(f => (
                  <td key={f} className="py-1 px-1">
                    <input type="number" value={b[f]} onChange={(e) => setT1(i, f, e.target.value)} readOnly={ro} disabled={ro}
                      className={`w-20 px-2 py-1 text-right tabular-nums border border-[#0D1B2A]/10 rounded bg-white ${ro ? "opacity-70 cursor-not-allowed" : ""}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Simple tables */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ST title="T2 Rose Cut" arr={rates.t2_rose} onChange={(i,f,v) => setSimpleTable("t2_rose", i, f, v)} />
        <ST title="T2 Fancy Cut" arr={rates.t2_fancy_cut} onChange={(i,f,v) => setSimpleTable("t2_fancy_cut", i, f, v)} />
        <ST title="T2 Fancy Colour" arr={rates.t2_fancy_colour} onChange={(i,f,v) => setSimpleTable("t2_fancy_colour", i, f, v)} />
        <ST title="T3 Briolette" arr={rates.t3_briolette} onChange={(i,f,v) => setSimpleTable("t3_briolette", i, f, v)} />
        <ST title="T4 Single Cut" arr={rates.t4_single} onChange={(i,f,v) => setSimpleTable("t4_single", i, f, v)} />
      </div>

      {/* History */}
      <div className="luxe-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-[#C9A84C]" />
          <h2 className="font-serif text-2xl text-[#0D1B2A]">Change history</h2>
        </div>
        <div className="divide-y divide-[#0D1B2A]/5">
          {history.length === 0 && <div className="py-6 text-sm text-slate-400">No previous changes.</div>}
          {history.map((h) => (
            <div key={`hist-${h.version}-${h.saved_at}`} className="py-3 flex justify-between text-sm">
              <span className="text-[#0D1B2A]">Version {h.version}</span>
              <span className="text-slate-500">{h.saved_by} · {new Date(h.saved_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="luxe-card p-6" data-testid={testid}>
      <h3 className="font-serif text-xl text-[#0D1B2A] mb-4">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, step = "1", testid, readOnly = false }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>
      <Input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} disabled={readOnly} className={`h-10 bg-white border-[#0D1B2A]/15 tabular-nums ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`} data-testid={testid} />
    </div>
  );
}

function SimpleTable({ title, arr, onChange, readOnly = false }) {
  return (
    <div className="luxe-card p-6 overflow-x-auto">
      <h3 className="font-serif text-xl text-[#0D1B2A] mb-4">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-2">Bracket</th><th className="py-2 px-1 text-right">Sell</th><th className="py-2 px-1 text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {arr.map((b, i) => (
            <tr key={`${b.min}-${b.max}-${i}`} className="border-t border-[#0D1B2A]/5">
              <td className="py-2 pr-2 text-slate-600 whitespace-nowrap">{b.min.toFixed(3)}–{b.max.toFixed(3)}</td>
              <td className="py-1 px-1"><input type="number" value={b.sell} onChange={(e) => onChange(i, "sell", e.target.value)} readOnly={readOnly} disabled={readOnly} className={`w-24 px-2 py-1 text-right border rounded border-[#0D1B2A]/10 bg-white ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`} /></td>
              <td className="py-1 px-1"><input type="number" value={b.cost} onChange={(e) => onChange(i, "cost", e.target.value)} readOnly={readOnly} disabled={readOnly} className={`w-24 px-2 py-1 text-right border rounded border-[#0D1B2A]/10 bg-white ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
